import { useContext, useEffect, useState } from "react";
import { GameContext } from "../context/GameContext";
import FormingWordLettersRecipient from "./FormingWordLettersRecipient";
import GuessedWordsRecipient from "./GuessedWordsRecipient";
import MatchLettersRecipient from "./MatchLettersRecipient";
import StatsChart from "./StatsChart";
import SubmitButton from "./SubmitButton";
import { toast } from "sonner";
import { timeReform } from "../../../../utils";
import { useStoreWordTrivia } from "../../../../api/trivia/useStoreWordTrivia";
import WordTriviaGameOverPrompt from "../../../../components/modals/WordTriviaGameOverPrompt";

export default function GameApp() {
  const {
    setMatch,
    percentCompleted,
    clueCounter,
    clueWord,
    handleClickClueBtn,
    timeLeft,
    triviaId,
    matchWords,
  } = useContext(GameContext);

  const {
    handleSubmitAnswer,
    submitting,
    showGameOver,
    rewards,
    error,
  } = useStoreWordTrivia();

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);

  useEffect(() => {
    if (clueWord) {
      toast.info(clueWord);
    }
  }, [clueWord]);

  // Submit when game completes (100%) - only if user actually completed it
  useEffect(() => {
    if (percentCompleted >= 1 && timeLeft > 0 && triviaId && !hasSubmitted) {
      setHasSubmitted(true);
      setGameEnded(true);
      handleSubmitAnswer(triviaId, percentCompleted, matchWords?.length || 0); // User completed all words
    }
  }, [
    percentCompleted,
    timeLeft,
    triviaId,
    hasSubmitted,
    handleSubmitAnswer,
    matchWords,
  ]);

  // Submit when timer runs out
  useEffect(() => {
    if (timeLeft === 0 && triviaId && !hasSubmitted) {
      setHasSubmitted(true);
      setGameEnded(true);
      handleSubmitAnswer(triviaId, percentCompleted, matchWords?.length || 0); // Time expired, incomplete
    }
  }, [
    timeLeft,
    triviaId,
    hasSubmitted,
    handleSubmitAnswer,
    percentCompleted,
    matchWords,
  ]);

  return (
    <>
      {submitting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-black">Submitting your results...</p>
          </div>
        </div>
      )}

      {(showGameOver || gameEnded) && (
        <WordTriviaGameOverPrompt
          rewards={rewards}
          isLoading={submitting}
          apiError={error}
          percentCompleted={percentCompleted}
          onPlayAgain={() => {
            window.history.back();
          }}
          onExit={() => {
            window.history.back();
          }}
        />
      )}

      <main className="flex flex-col">
        {percentCompleted === 100 && !showGameOver && !gameEnded ? (
          <>
            <menu className="animate__bounceIn mb-3 self-center">
              <button
                onClick={() => {
                  setGameEnded(false);
                  setHasSubmitted(false); // Reset submission flag
                  setMatch();
                }}
                className="p-2 gradients text-white rounded"
              >
                <i className="bi bi-plus" />
                Play new match
              </button>
            </menu>
            <StatsChart />
          </>
        ) : (
          <>
            <section className="statsSection animate__bounceIn">
              <menu className="flex justify-between items-center mt-2 ">
                <p className="text-white">
                  {percentCompleted.toFixed(2) * 100}% completed
                </p>
                <div className="text-white text-right">
                  <span className="text-sm text-white opacity-80">Time</span>
                  <div className="text-lg text-white font-bold">
                    {timeReform(timeLeft)}
                  </div>
                </div>
                <p className="remainingClues text-white">
                  Remaining clues: {clueCounter}
                </p>
              </menu>
            </section>

            <section className="gameplaySection flex justify-between">
              <section className="sectionA flex flex-col w-full">
                <GuessedWordsRecipient />
              </section>

              <section className="sectionB w-full flex flex-col">
                <FormingWordLettersRecipient />
                <div className="flex justify-between items-end mt-4">
                  <MatchLettersRecipient />
                  <div>
                    <button
                      title="HINT"
                      onClick={handleClickClueBtn}
                      className="w-full block animate__bounceIn px-2 shadow-md py-1 text-primary-main glassy rounded-lg"
                    >
                      <span className="italic text-lg font-semibold">A</span>
                      <i className="bi bi-info text-sm" />
                    </button>
                  </div>
                </div>
                <div className="mt-5">
                  <SubmitButton />
                </div>
              </section>
            </section>
          </>
        )}
      </main>
    </>
  );
}
